/**
 * Which Mapbox projection to draw with, decided from the machine's renderer.
 *
 * A globe is real 3D — sphere geometry plus an atmosphere pass — and costs far
 * more to draw than flat Mercator. On anything with a working GPU that is free
 * and the globe is the look we want. On a machine falling back to a software
 * rasteriser it is not: the map crawls, and everything sharing the main thread
 * crawls with it.
 *
 * Software rendering isn't only old hardware. It's virtual desktops (Citrix,
 * RDP, VMware), machines whose graphics driver Chrome has blocklisted, locked
 * down corporate builds — and CI agents, which have no GPU at all. Freight
 * customers work on exactly those kinds of desktops more often than average.
 *
 * Public rather than `Internal/` for the same reason as `mapboxStyleUrl`: the
 * dashboard's ShipmentMap builds its own Mapbox instance and has to make the
 * same choice, and the two must not drift.
 */

/**
 * Renderer names that mean "no GPU, drawing on the CPU". Software rasterisers
 * identify themselves plainly, which is what makes this check workable:
 * SwiftShader is Chrome's, llvmpipe and softpipe are Mesa's, "Microsoft Basic
 * Render Driver" is Windows' fallback adapter.
 */
const SOFTWARE_RENDERER =
  /swiftshader|llvmpipe|softpipe|software|basic render|mesa offscreen|generic renderer/i;

/**
 * True when the renderer string names a software rasteriser.
 *
 * An absent string is treated as hardware. The string comes from
 * `WEBGL_debug_renderer_info`, which Firefox can withhold, and the failure
 * modes are asymmetric: guessing "hardware" on a software machine costs a slow
 * map, while guessing "software" everywhere the extension is missing would drop
 * the globe for a whole browser's users who mostly have perfectly good GPUs.
 */
export function isSoftwareRenderer(renderer: string | undefined): boolean {
  if (renderer === undefined || renderer === "") {
    return false;
  }
  return SOFTWARE_RENDERER.test(renderer);
}

/**
 * The unmasked renderer name, or undefined when it can't be read — no WebGL at
 * all (jsdom, WebGL disabled) or the debug extension withheld.
 */
function detectRenderer(): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  try {
    const canvas = document.createElement("canvas");
    // Truthiness rather than `=== null` throughout: the WebGL API is specified
    // to return null when a context or extension is unavailable, but headless
    // and emulated environments return undefined instead, and either way there
    // is nothing to read.
    const gl =
      canvas.getContext("webgl2") ??
      (canvas.getContext("webgl") as WebGLRenderingContext | undefined);
    if (!gl) {
      return undefined;
    }
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : gl.getParameter(gl.RENDERER);
    // Hand the context straight back. Browsers cap how many are live at once,
    // and this probe must never be the reason a real map can't get one.
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return typeof renderer === "string" ? renderer : undefined;
  } catch {
    return undefined;
  }
}

/** Probed once — the answer can't change within a page load. */
let cachedProjection: "globe" | "mercator" | undefined;

/**
 * `globe` on a machine with a GPU, `mercator` on one without.
 *
 * Pinned explicitly either way rather than left to the style: only the current
 * generation of Mapbox styles defaults to a globe, so an unpinned map flips
 * projection when the style pair changes — which is how light mode once
 * rendered flat while dark mode rendered as a globe.
 */
export function mapboxProjection(): "globe" | "mercator" {
  cachedProjection ??= isSoftwareRenderer(detectRenderer())
    ? "mercator"
    : "globe";
  return cachedProjection;
}

/** Test seam: forget the probed value so a test can re-run the detection. */
export function resetMapboxProjectionCacheForTests(): void {
  cachedProjection = undefined;
}
